import React, { Component } from 'react';
import './ErrorBoundary.css';

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI.
 */
class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            eventId: null
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render shows the fallback UI
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // Log error to console
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        this.setState({ errorInfo });

        // Send to error tracking service (Sentry, etc.)
        if (window.Sentry) {
            window.Sentry.withScope((scope) => {
                scope.setExtras(errorInfo);
                const eventId = window.Sentry.captureException(error);
                this.setState({ eventId });
            });
        }

        // Send to custom error logging endpoint
        this.logErrorToService(error, errorInfo);
    }

    logErrorToService = async (error, errorInfo) => {
        try {
            // Optional: Send error to your backend
            await fetch('/api/log-error', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    error: error.toString(),
                    stack: error.stack,
                    componentStack: errorInfo?.componentStack,
                    url: window.location.href,
                    userAgent: navigator.userAgent,
                    timestamp: new Date().toISOString()
                })
            }).catch(() => {
                // Silently fail if error logging endpoint doesn't exist
            });
        } catch {
            // Ignore logging errors
        }
    };

    handleReload = () => {
        window.location.reload();
    };

    handleGoHome = () => {
        window.location.href = '/';
    };

    handleReportFeedback = () => {
        if (window.Sentry && this.state.eventId) {
            window.Sentry.showReportDialog({ eventId: this.state.eventId });
        }
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback UI
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="error-boundary">
                    <div className="error-boundary__container">
                        <div className="error-boundary__icon">
                            <i className="fas fa-exclamation-triangle"></i>
                        </div>

                        <h1 className="error-boundary__title">
                            Bir Şeyler Ters Gitti
                        </h1>

                        <p className="error-boundary__message">
                            Üzgünüz, beklenmeyen bir hata oluştu. Ekibimiz bu sorundan haberdar edildi.
                        </p>

                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details className="error-boundary__details">
                                <summary>Hata Detayları (Geliştirici)</summary>
                                <pre>{this.state.error.toString()}</pre>
                                {this.state.errorInfo && (
                                    <pre>{this.state.errorInfo.componentStack}</pre>
                                )}
                            </details>
                        )}

                        <div className="error-boundary__actions">
                            <button
                                className="error-boundary__btn error-boundary__btn--primary"
                                onClick={this.handleReload}
                            >
                                <i className="fas fa-redo"></i>
                                Sayfayı Yenile
                            </button>

                            <button
                                className="error-boundary__btn error-boundary__btn--secondary"
                                onClick={this.handleGoHome}
                            >
                                <i className="fas fa-home"></i>
                                Ana Sayfaya Dön
                            </button>

                            {window.Sentry && this.state.eventId && (
                                <button
                                    className="error-boundary__btn error-boundary__btn--outline"
                                    onClick={this.handleReportFeedback}
                                >
                                    <i className="fas fa-bug"></i>
                                    Hata Bildir
                                </button>
                            )}
                        </div>

                        <p className="error-boundary__support">
                            Sorun devam ederse: <a href="mailto:destek@orderpanel.com.tr">destek@orderpanel.com.tr</a>
                        </p>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
